/*
 * Authentication actions
 *
 * runAuthProvider
 *
 * List of redirects:
 * - signInRequest
 * - signInRedirectCallback
 *
 * - signInSilentCallback
 *
 * - loginRequest
 * - refreshToken
 *
 * List of warning:
 * - userIdleWarning
 *
 * List of utils:
 * - redirectTo
 * - redirectToLogin
 * - redirectToSignIn
 *
 * Logs:
 * - remoteLog
 */

import {
  ASYNC_DOWNLOAD_DISPATCHER_PREFIX
} from '../../dispatcher/config/Constants';
import {
  APP_SETTINGS
} from '../../dispatcher/authentication/services/AuthSettings';
import {
  ActivityWarning
} from '../../dispatcher/modules/warning/content/activity/ActivityWarning';
import {
  CookiesWarning
} from '../../dispatcher/modules/warning/content/cookie/CookiesWarning';
import Configuration from '../../dispatcher/config/Configuration';
// noinspection TypeScriptPreferShortImport
import {
  Action,
  Position,
  OperationType,
  Stop,
  WarningType,
  WindowType
} from '../../dispatcher/Types';

/**
 * Initialize auth provider and load application
 */
export const runAuthProvider: Action = [{
  data: {
    firstLoadApp: true
  },
  persistent: ['firstLoadApp'],
  type: OperationType.data
}, {
  name: 'maskTopLevelComponent',
  type: OperationType.action
}, {
  moduleName: 'AuthService',
  name: 'runService'
}, {
  isCallback: false,
  isPromise: true,
  moduleName: 'AuthProvider',
  name: 'runProvider'
}, {
  name: 'loadApplication',
  type: OperationType.action
}, {
  name: 'unmaskTopLevelComponent',
  type: OperationType.action
}];

/**
 * Cleanup after invalidating/removing user
 */
export const cleanup: Action = [{
  moduleName: 'ActivityService',
  name: 'suspendActivity'
}, {
  moduleName: 'PopupWindow',
  name: 'close'
}, {
  name: 'closeAndUnlinkDownloadIndicatorWidget',
  type: OperationType.action
}];

export const stopAll: Action = [{
  items: [Stop.stacks, Stop.operations],
  dispatcherIds: [ASYNC_DOWNLOAD_DISPATCHER_PREFIX],
  partialMatch: true,
  type: OperationType.stopAll
}];

export const setLandingUri: Action = [{
  moduleName: 'AuthService',
  name: 'getLandingUri',
  mappingName: 'path',
  type: OperationType.addOperationData
}, {
  data: {
    template: {
      bind: {
        path: '{path}'
      }
    }
  },
  type: OperationType.redirect
}];

export const firstLoadAppUnmask: Action = [{
  condition: '{firstLoadApp}',
  conditionOperations: [{
    persistent: ['firstLoadApp'],
    type: OperationType.removePersistentData
  }, {
    name: 'unmaskTopLevelComponent',
    type: OperationType.action
  }],
  type: OperationType.conditional
}];

/**
 * Warn user that he/she has already been logged out
 * This can happen if user has signed out from other SSO enabled application
 */
export const userChangedWarning: Action = [{
  className: 'sm footer-centered',
  items: [{
    operations: [{
      data: {
        path: '',
        forceRefresh: true
      },
      type: OperationType.redirect
    }],
    btnName: 'Auth.userChangedWarning.ok',
    btnConfig: {
      pos: Position.end,
      themeColor: 'primary'
    }
  }, {
    btnName: 'ESCAPE',
    operations: [{
      data: {
        path: '',
        forceRefresh: true
      },
      type: OperationType.redirect
    }]
  }],
  text: 'Auth.userChangedWarning.text',
  title: 'Auth.userChangedWarning.title',
  type: OperationType.warning,
  warningType: WarningType.warning
}];

/**
 * Warn user that he/she has already been logged out
 * This can happen if user has signed out from other SSO enabled application
 */
export const systemClockWarning: Action = [{
  className: 'sm footer-centered',
  items: [{
    operations: [{
      data: {
        path: APP_SETTINGS.login_uri,
        forceRefresh: true
      },
      type: OperationType.redirect
    }],
    btnName: 'Auth.systemClockWarning.ok',
    btnConfig: {
      pos: Position.end,
      themeColor: 'primary'
    }
  }],
  text: 'Auth.systemClockWarning.text',
  title: 'Auth.systemClockWarning.title',
  type: OperationType.warning,
  warningType: WarningType.warning
}];

/**
 * Warn user that he/she has already been logged out
 * This can happen if user has signed out from other SSO enabled application
 */
export const systemSetupWarning: Action = [{
  children: [{
    render: CookiesWarning,
    propArgs: ['data', 'config']
  }],
  closable: false,
  className: 'sm footer-centered',
  items: [{
    operations: [{
      isCallback: false,
      isPromise: true,
      moduleName: 'AuthService',
      name: 'removeUser'
    }, {
      moduleName: 'AuthService',
      name: 'finishSignOut'
    }, {
      data: {
        path: APP_SETTINGS.login_uri
      },
      type: OperationType.redirect
    }],
    btnName: 'CookiesWarning.reload',
    btnConfig: {
      themeColor: 'primary',
      pos: Position.end
    }
  }],
  title: 'CookiesWarning.title',
  type: OperationType.dialog
}, {
  type: OperationType.stopAll
}];

/**
 * Warn user that he/she has been idle
 */
export const userIdleWarning: Action = [{
  isCallback: false,
  isPromise: true,
  moduleName: 'AuthService',
  name: 'isAuthenticated',
  type: OperationType.addOperationData
}, {
  condition: '{isAuthenticated}',
  conditionOperations: [{
    moduleName: 'ActivityService',
    name: 'suspendActivity'
  }, {
    children: [{
      render: ActivityWarning,
      propArgs: ['data', 'config']
    }],
    closable: false,
    className: 'sm footer-centered',
    items: [{
      operations: [{
        moduleName: 'ActivityService',
        name: 'resumeActivity'
      }, {
        windowType: WindowType.dialog,
        type: OperationType.closeWindow
      }],
      btnName: 'ActivityWarning.ok',
      btnConfig: {
        themeColor: 'primary',
        pos: Position.end
      }
    }],
    title: 'ActivityWarning.title',
    type: OperationType.dialog
  }],
  type: OperationType.conditional
}];

/**
 * Redirect application
 */
export const redirectTo: Action = [{
  data: {
    template: {
      bind: {
        path: '{path}'
      }
    }
  },
  type: OperationType.redirect
}];

/**
 * Redirect application to login page
 */
export const redirectToLogin: Action = [{
  data: {
    path: APP_SETTINGS.login_uri
  },
  type: OperationType.redirect
}];

/**
 * Run auth service
 */
export const runAuthService: Action = [{
  moduleName: 'AuthService',
  name: 'runService'
}];

/**
 * Send email action
 */
export const sendErrorCodeEmail: Action = [{
  data: {
    template: {
      bind: {
        email: '_.get({initializeUserData}, "administratorEmail")'
      }
    }
  },
  type: OperationType.data
}, {
  condition: '{email}',
  conditionOperations: [{
    data: {
      key: 'email.error.subject'
    },
    type: OperationType.data
  }, {
    data: {
      template: {
        options: {
          appUrl: Configuration.get().REST_SERVER.DOMAIN,
          bind: {
            errorCode: '{errorCode}'
          }
        }
      }
    },
    type: OperationType.data
  }, {
    args: ['key', 'options'],
    moduleName: 'AuthProvider',
    name: 'translateByNamespace',
    mappingName: 'subject',
    type: OperationType.addOperationData
  }, {
    data: {
      key: 'email.error.content'
    },
    type: OperationType.data
  }, {
    args: ['key'],
    moduleName: 'AuthProvider',
    name: 'translateByNamespace',
    mappingName: 'content',
    type: OperationType.addOperationData
  }, {
    args: ['email', 'subject', 'content'],
    name: 'sendEmail',
    moduleName: 'AuthService'
  }],
  elseOperations: [{
    name: 'adminMailNotSetWarning',
    type: OperationType.action
  }],
  type: OperationType.conditional
}];

/**
 * Warn user that admin email is not set
 */
export const adminMailNotSetWarning: Action = [{
  className: 'sm footer-centered',
  items: [{
    operations: [],
    btnName: 'Email.adminMailNotSetWarning.ok',
    btnConfig: {
      pos: Position.end,
      themeColor: 'primary'
    }
  }],
  text: 'Email.adminMailNotSetWarning.text',
  title: 'Email.adminMailNotSetWarning.title',
  type: OperationType.warning,
  warningType: WarningType.warning
}];

/**
 * Log any error to remote log endpoint
 */
export const remoteLog: Action = [{
  data: {
    template: {
      bind: {
        url: '{url}'
      }
    }
  },
  type: OperationType.request
}];
